import type { SearchRow } from '@bloo-team/api-client'

/**
 * The causality (process) tree behind the deep panel (decision S5): when a
 * raw-event result carries sysmon-style parent/child fields, rows fold into
 * a parent -> child forest. Pure and data-dependent — a result without the
 * fields yields null and the section hides.
 *
 * Keying: ProcessId/ParentProcessId when BOTH are present, otherwise the
 * Image/ParentImage names (weaker — every "cmd.exe" collapses to one node,
 * but still reads as a chain). A parent that no row describes as a child
 * becomes a synthetic root so the chain never loses its head.
 */

export interface ProcessNode {
  /** Unique within one tree build. */
  id: string
  image: string
  commandLine: string | null
  pid: string | null
  /** True for a parent only known from its children's Parent* fields. */
  synthetic: boolean
  /** The row this node came from; null for synthetic parents. */
  row: SearchRow | null
  children: ProcessNode[]
}

export interface ProcessColumns {
  image: string | null
  pid: string | null
  cmd: string | null
  parentImage: string | null
  parentPid: string | null
  parentCmd: string | null
}

const CHILD_FIELDS: Record<'image' | 'pid' | 'cmd', string[]> = {
  image: ['image', 'processname', 'process'],
  pid: ['processid', 'pid'],
  cmd: ['commandline', 'cmdline'],
}
const PARENT_FIELDS: Record<'parentImage' | 'parentPid' | 'parentCmd', string[]> = {
  parentImage: ['parentimage', 'parentprocessname', 'parentprocess'],
  parentPid: ['parentprocessid', 'ppid', 'parentpid'],
  parentCmd: ['parentcommandline', 'parentcmdline'],
}

function find(columns: readonly string[], names: readonly string[]): string | null {
  const lower = new Map(columns.map((c) => [c.toLowerCase().replace(/^\$/, ''), c]))
  for (const n of names) {
    const hit = lower.get(n)
    if (hit) return hit
  }
  return null
}

/** Resolve the parent/child columns case-insensitively; null when absent. */
export function processColumns(columns: readonly string[]): ProcessColumns {
  return {
    image: find(columns, CHILD_FIELDS.image),
    pid: find(columns, CHILD_FIELDS.pid),
    cmd: find(columns, CHILD_FIELDS.cmd),
    parentImage: find(columns, PARENT_FIELDS.parentImage),
    parentPid: find(columns, PARENT_FIELDS.parentPid),
    parentCmd: find(columns, PARENT_FIELDS.parentCmd),
  }
}

/** True when the result carries at least one parent AND one child field. */
export function hasProcessFields(columns: readonly string[]): boolean {
  const c = processColumns(columns)
  return !!(c.parentImage || c.parentPid || c.parentCmd) && !!(c.image || c.pid || c.cmd)
}

const text = (v: unknown): string | null => {
  if (v === null || v === undefined || typeof v === 'boolean') return null
  const s = String(v).trim()
  return s ? s : null
}

/** Last path segment — `C:\Windows\System32\cmd.exe` reads as `cmd.exe`. */
export function baseName(path: string): string {
  const parts = path.split(/[\\/]/)
  return parts[parts.length - 1] || path
}

/**
 * Build the forest. Returns null when the columns lack the fields (the
 * section should hide), an empty array when the fields exist but no row
 * populates them.
 *
 * Deterministic: roots and children keep row order; synthetic parents sort
 * where their first child appeared.
 */
export function buildProcessTree(rows: readonly SearchRow[], columns: readonly string[]): ProcessNode[] | null {
  if (!hasProcessFields(columns)) return null
  const c = processColumns(columns)
  const byPid = !!(c.pid && c.parentPid)

  const nodes: ProcessNode[] = []
  const byKey = new Map<string, ProcessNode>()
  const parentKeyOf = new Map<string, string | null>()

  rows.forEach((row, i) => {
    const image = (c.image && text(row[c.image])) ?? null
    const pid = (c.pid && text(row[c.pid])) ?? null
    const cmd = (c.cmd && text(row[c.cmd])) ?? null
    const key = byPid ? pid : image
    if (!key && !cmd) return
    const parentKey = byPid
      ? (c.parentPid && text(row[c.parentPid])) ?? null
      : (c.parentImage && text(row[c.parentImage])) ?? null
    const node: ProcessNode = {
      id: `r${i}`,
      image: image ?? (cmd ? baseName(cmd.split(' ')[0] ?? cmd) : `pid ${pid ?? '?'}`),
      commandLine: cmd,
      pid,
      synthetic: false,
      row,
      children: [],
    }
    nodes.push(node)
    // First row wins a key: two rows with one pid (a restart) both render,
    // but children hang off the first.
    if (key && !byKey.has(key)) byKey.set(key, node)
    parentKeyOf.set(node.id, parentKey && parentKey !== key ? parentKey : null)
  })

  const roots: ProcessNode[] = []
  const synthetic = new Map<string, ProcessNode>()
  for (const node of nodes) {
    const pk = parentKeyOf.get(node.id) ?? null
    if (!pk) {
      roots.push(node)
      continue
    }
    let parent = byKey.get(pk) ?? synthetic.get(pk)
    if (!parent) {
      // An orphan: the parent is only described by the child's Parent* fields.
      const row = node.row!
      const pImage = c.parentImage ? text(row[c.parentImage]) : null
      const pCmd = c.parentCmd ? text(row[c.parentCmd]) : null
      parent = {
        id: `p${synthetic.size}`,
        image: pImage ?? (byPid ? `pid ${pk}` : pk),
        commandLine: pCmd,
        pid: byPid ? pk : (c.parentPid ? text(row[c.parentPid]) : null),
        synthetic: true,
        row: null,
        children: [],
      }
      synthetic.set(pk, parent)
      roots.push(parent)
    }
    parent.children.push(node)
  }
  // A row-described node can be reached from a root AND sit in `roots` only
  // when it had no parent key — so no node appears twice. Drop any root that
  // a cycle made reachable from another root (pid reuse), keeping order.
  const reachable = new Set<string>()
  const walk = (n: ProcessNode, depth: number) => {
    if (depth > 64) return
    for (const ch of n.children) {
      reachable.add(ch.id)
      walk(ch, depth + 1)
    }
  }
  for (const r of roots) walk(r, 0)
  return roots.filter((r) => !reachable.has(r.id))
}

/** Total node count of a forest — the section header count. */
export function countNodes(forest: readonly ProcessNode[]): number {
  let n = 0
  const walk = (list: readonly ProcessNode[]) => {
    for (const x of list) {
      n += 1
      walk(x.children)
    }
  }
  walk(forest)
  return n
}
